import React from 'react';

// 40-week pregnancy milestones (ISO dates computed off due date)
export const MILESTONES = {
  4: '4-week: First missed period; positive pregnancy test possible',
  5: '5-week: Embryonic heart begins to develop',
  6: '6-week: Heartbeat may be detected on ultrasound',
  8: '8-week: First prenatal visit typical; fingers/toes forming',
  10: '10-week: Fetal period begins; all vital organs formed',
  12: '12-week: End of 1st trimester; miscarriage risk drops sharply',
  13: '13-week: Baby can make facial expressions',
  16: '16-week: Second-trimester screening (Quad screen)',
  18: '18-week: Baby can hear sounds from outside',
  20: '20-week: Anatomy ultrasound; gender reveal possible',
  22: '22-week: Baby has recognisable eyebrows and eyelashes',
  24: '24-week: Viability threshold reached',
  26: '26-week: Baby can respond to light',
  28: '28-week: Third trimester begins; glucose-tolerance test',
  30: '30-week: Rapid brain and eye development',
  32: "32-week: Baby's movements increase; head-down position typical",
  34: '34-week: Baby practices breathing movements',
  36: "36-week: Baby considered 'early term' next week",
  37: '37-week: Full term begins',
  38: '38-week: Baby ready for birth; final weight gain',
  39: '39-week: Optimal delivery week',
  40: '40-week: Due date — Estimated Date of Delivery (EDD)',
};

export const isImportant = (w) => [4, 8, 12, 16, 20, 24, 28, 32, 36, 37, 40].includes(w);

const fmt = d => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

/* Full 40-week table with per-week date and highlighted milestones */
export function FullPregnancyTable({ dueDate, currentWeek, testId = 'preg-full-table' }) {
  return (
    <div className="mt-4">
      <h4 className="font-bold text-slate-900 mb-2 text-sm">Full Week-by-Week Table (Weeks 1 — 40) · milestones highlighted</h4>
      <div className="overflow-x-auto max-h-96 border border-slate-300 rounded">
        <table className="w-full text-xs" data-testid={testId}>
          <thead className="bg-[#1e40af] text-white sticky top-0">
            <tr>
              <th className="px-2 py-1.5 text-left">Week</th>
              <th className="px-2 py-1.5 text-left">Trimester</th>
              <th className="px-2 py-1.5 text-left">Date</th>
              <th className="px-2 py-1.5 text-left">Development / Milestone</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 40 }, (_, i) => i + 1).map((w) => {
              const dt = addDays(dueDate, -(40 - w) * 7);
              const important = isImportant(w);
              const isCurrent = w === currentWeek;
              const cls = isCurrent ? 'bg-emerald-100 font-bold' : important ? 'bg-amber-50' : (w % 2 ? 'bg-slate-50' : '');
              const label = MILESTONES[w] || (w < 13 ? 'First trimester development' : w < 27 ? 'Second trimester growth' : 'Third trimester preparation');
              return (
                <tr key={w} className={cls}>
                  <td className="px-2 py-1">Week {w}{isCurrent ? ' ★' : ''}</td>
                  <td className="px-2 py-1">{w < 13 ? '1st' : w < 27 ? '2nd' : '3rd'}</td>
                  <td className="px-2 py-1 font-mono">{fmt(dt)}</td>
                  <td className={`px-2 py-1 ${important ? 'text-slate-900' : 'text-slate-500'}`}>{important ? <b>{label}</b> : label}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500 mt-1">
        <span className="inline-block w-3 h-3 bg-amber-50 border border-amber-200 mr-1" /> Important milestones ·
        <span className="inline-block w-3 h-3 bg-emerald-100 border border-emerald-300 mx-1" /> Current week (★) ·
        Dates computed from your due date.
      </div>
    </div>
  );
}

/* Reusable cycle calendar (28-35 days) with period/fertile/ovulation coloring */
export function CycleCalendar({ lmpDate, cycleLen, ovulationDay = null, testId = 'cycle-calendar' }) {
  const n = +cycleLen || 28;
  const ov = ovulationDay || (n - 14);
  const days = Array.from({ length: n }, (_, i) => {
    const d = addDays(lmpDate, i);
    const isPeriod = i < 5;
    const isOvulation = i === ov;
    const isFertile = i >= ov - 5 && i <= ov + 1 && !isOvulation;
    return { d, i, isPeriod, isFertile, isOvulation };
  });
  return (
    <div className="mt-6">
      <h3 className="font-bold text-slate-900 mb-2">Cycle Calendar (day 1 = first day of period)</h3>
      <div className="flex flex-wrap items-center gap-3 text-xs mb-3">
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 bg-rose-300 rounded" /> Period days (1-5)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 bg-emerald-200 rounded" /> Fertile window</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 bg-emerald-500 rounded" /> Ovulation day</span>
      </div>
      <div className="grid grid-cols-7 gap-1.5" data-testid={testId}>
        {days.map(({ d, i, isPeriod, isFertile, isOvulation }) => {
          const bg = isOvulation ? 'bg-emerald-500 text-white' : isFertile ? 'bg-emerald-200' : isPeriod ? 'bg-rose-300' : 'bg-slate-50';
          return (
            <div key={i} className={`p-2 rounded border border-slate-200 text-center ${bg}`}>
              <div className="text-xs font-semibold">Day {i + 1}</div>
              <div className="text-xs">{d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
              {isOvulation && <div className="text-[10px] font-bold mt-0.5">OVULATION</div>}
              {isPeriod && i === 0 && <div className="text-[10px] font-bold mt-0.5">LMP</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
